/**
 * useTableParams — Manages **table-scoped** query params (pagination, sorting, filters, search, column visibility).
 *
 * Important:
 * - Handles only **table-local** filters/state (search, status, category, columnsVisibility).
 * - **Excludes page-wide date filters** (`start_date`, `end_date`, `dates_logic`); those live in a global store
 *   and are merged into the request at fetch time.
 * - Persists session params (page, sort, filters, search) to **sessionStorage** — survives SPA navigation
 *   but is cleared on tab close or page reload.
 * - Persists **columnsVisibility** to **localStorage** — survives reloads as a user preference.
 * - Accepts an optional `watchId` to scope session params to a specific entity (e.g. a user or POS id).
 *   When `watchId` changes, session params are reset to defaults so stale state from a previous entity
 *   is never shown.
 * - Accepts an optional `globalFiltersSignature`: a stable **string** identity of the page-wide filters
 *   (date, entities, session filters), built with `stable()`. When it changes, pagination goes back to
 *   page 1 — otherwise a filter change would keep requesting whatever page the user was on, which may
 *   not exist in the new result set.
 *
 * @param tableId           Unique key used to persist/restore params per table.
 * @param initialColumns    (Optional) Columns to initialize `columnsVisibility` (default: all visible).
 * @param options           { persist?: boolean; watchId?: string | number | boolean; globalFiltersSignature?: string }
 * @param initial           (Optional) Partial overrides for default params on first load.
 *
 * @returns {
 *   tableParams: TableParams;
 *   updateTableParams(newParams): void;
 *   handleTableChange(pagination, filters, sorter): void;
 *   handleSearch(term): void;
 *   clearFilters(): void;
 *   updateFilters(partial): void;
 *   updateColumnsVisibility(map): void;
 * }
 */

import { Filter } from '@/legacy/types';
import { TablePaginationConfig } from 'antd';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { useCallback, useEffect, useRef, useState } from 'react';
import { TableColumn, TableParams } from '../types';
import { CURRENT_TABLE_VERSION } from '../constants';
import {
	readSessionParams,
	writeSessionParams,
	removeSessionParams,
	readColumnsVisibility,
	writeColumnsVisibility,
	readMetaVersion,
	writeMetaVersion,
} from '../utils';

type UseTableParamsOptions = {
	persist?: boolean;
	watchId?: string | number | boolean;
	globalFiltersSignature?: string;
};

type SessionParams = Omit<TableParams, 'columnsVisibility'> & {
	watchId?: string | number | boolean;
};

const isPositiveInt = (value: unknown): value is number =>
	typeof value === 'number' && Number.isInteger(value) && value > 0;

const initializeColumnVisibility = <T,>(columns: TableColumn<T>[]): Record<string, boolean> => {
	const visibility: Record<string, boolean> = {};

	columns.forEach((col) => {
		if (!col.dataIndex) return;
		const key = col.dataIndex.toString();
		const hide = (col as any).initial_hidden === true || (col as any).initialHidden === true;
		visibility[key] = !hide;
	});
	return visibility;
};

export const useTableParams = <T,>(
	tableId: string,
	initialColumns?: TableColumn<T>[],
	options?: UseTableParamsOptions,
	initial?: Partial<TableParams>,
) => {
	const initialParamsRef = useRef<TableParams | null>(null);
	const prevWatchIdRef = useRef(options?.watchId);

	const persist = options?.persist ?? true;
	const watchId = options?.watchId;
	const globalFiltersSignature = options?.globalFiltersSignature;

	const getDefaultParams = (): TableParams => ({
		page: 1,
		pageSize: 10,
		sortBy: '',
		direction: null,
		filters: {},
		columnsVisibility: initializeColumnVisibility(initialColumns ?? []),
		search: '',
		version: CURRENT_TABLE_VERSION[tableId as keyof typeof CURRENT_TABLE_VERSION] || '1.0.0',
		...initial,
	});

	const loadInitial = (): TableParams => {
		if (initialParamsRef.current) return initialParamsRef.current;

		const defaults = getDefaultParams();
		const currentVersion = defaults.version;

		// Version check — clear stale session data when schema changes
		const prevVersion = readMetaVersion(tableId);
		if (prevVersion !== currentVersion) {
			removeSessionParams(tableId);
			writeMetaVersion(tableId, currentVersion);
		}

		// Restore column visibility from localStorage (user preference, survives reloads)
		const savedVisibility = readColumnsVisibility(tableId);
		if (savedVisibility) {
			defaults.columnsVisibility = { ...defaults.columnsVisibility, ...savedVisibility };
		}

		// Restore session params from sessionStorage (survives navigation, not reloads)
		if (persist) {
			const saved = readSessionParams<SessionParams>(tableId);
			if (saved?.version === currentVersion) {
				const watchIdMatches = watchId === undefined || saved.watchId === watchId;
				if (watchIdMatches) {
					const hasFilters = saved.filters && Object.keys(saved.filters).length > 0;
					initialParamsRef.current = {
						...defaults,
						// sessionStorage is untyped at runtime; a corrupt value would be sent to the API as-is
						page: isPositiveInt(saved.page) ? saved.page : defaults.page,
						pageSize: isPositiveInt(saved.pageSize) ? saved.pageSize : defaults.pageSize,
						sortBy: saved.sortBy || defaults.sortBy,
						direction: saved.direction ?? defaults.direction,
						filters: hasFilters ? saved.filters : defaults.filters,
						search: saved.search || defaults.search,
					};
					return initialParamsRef.current;
				}
			}
		}

		initialParamsRef.current = defaults;
		return defaults;
	};

	const [params, setParams] = useState<TableParams>(loadInitial);

	const writeSession = useCallback(
		(updated: TableParams) => {
			if (!persist) return;
			const { columnsVisibility: _, ...sessionPart } = updated;
			writeSessionParams<SessionParams>(tableId, { ...sessionPart, watchId });
		},
		[persist, tableId, watchId],
	);

	// Reset params when watchId changes (e.g. navigating from one user/POS detail to another
	// without unmounting, such as in a tab view)
	useEffect(() => {
		if (watchId === undefined) return;
		const prev = prevWatchIdRef.current;
		prevWatchIdRef.current = watchId;
		if (prev === watchId) return;

		setParams((current) => {
			const reset = {
				...current,
				page: 1,
				filters: {},
				search: '',
				sortBy: '',
				direction: null,
			} as TableParams;
			removeSessionParams(tableId);
			return reset;
		});
	}, [watchId, tableId]);

	const [lastSignature, setLastSignature] = useState(globalFiltersSignature);
	const pendingSessionWriteRef = useRef(false);

	if (globalFiltersSignature !== lastSignature) {
		setLastSignature(globalFiltersSignature);
		if (params.page !== 1) {
			setParams((prev) => ({ ...prev, page: 1 }));
			pendingSessionWriteRef.current = true;
		}
	}

	useEffect(() => {
		if (!pendingSessionWriteRef.current) return;
		pendingSessionWriteRef.current = false;
		writeSession(params);
	}, [params, writeSession]);

	const updateParams = useCallback(
		(newParams: Partial<TableParams>) => {
			setParams((prev) => {
				const updated = { ...prev, ...newParams };
				writeSession(updated);
				return updated;
			});
		},
		[writeSession],
	);

	const handleTableChange = (
		pagination: TablePaginationConfig,
		antDFilters: Record<string, FilterValue | null>,
		sorter: SorterResult<any> | SorterResult<any>[],
	) => {
		if (!Array.isArray(sorter)) {
			const transformedFilters: Filter = {};
			Object.entries(antDFilters).forEach(([key, value]) => {
				if (value != null) {
					transformedFilters[key] =
						Array.isArray(value) && value.length === 1
							? (value[0] as string | number | boolean | object | Date | undefined)
							: (value as string | number | boolean | object | Date | undefined);
				}
			});

			updateParams({
				page: pagination.current ?? 1,
				pageSize: pagination.pageSize ?? 10,
				sortBy: sorter.field?.toString() || '',
				direction: sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : null,
				filters: {
					...params.filters,
					...transformedFilters,
				},
			});
		}
	};

	const handleSearch = (searchTerm: string) => {
		setParams((prev) => {
			if (prev.search === searchTerm) return prev;
			const updated = { ...prev, search: searchTerm, page: 1 };
			writeSession(updated);
			return updated;
		});
	};

	const clearFilters = useCallback(() => {
		setParams((prev) => {
			const updated = { ...prev, filters: {}, page: 1 };
			writeSession(updated);
			return updated;
		});
	}, [writeSession]);

	const isEmptyRangeFilter = (value: unknown): boolean => {
		if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
		if (!('isRange' in value)) return false;
		const rangeValue = value as { min?: unknown; max?: unknown; isRange?: boolean };
		const hasMin = rangeValue.min !== null && rangeValue.min !== undefined;
		const hasMax = rangeValue.max !== null && rangeValue.max !== undefined;
		return !hasMin && !hasMax;
	};

	const updateFilters = (newFilters: Partial<Filter>) => {
		setParams((prev) => {
			const cleanedNewFilters: Partial<Filter> = {};

			Object.entries(newFilters).forEach(([key, value]) => {
				if (
					value === undefined ||
					value === null ||
					(value instanceof Array && value.length === 0) ||
					isEmptyRangeFilter(value)
				) {
					/* empty */
				} else {
					cleanedNewFilters[key as keyof Filter] = value;
				}
			});

			const newCombinedFilters = { ...prev.filters };

			Object.keys(prev.filters ?? {}).forEach((key) => {
				if (
					newFilters[key] === undefined ||
					(Array.isArray(newFilters[key]) && newFilters[key]?.length === 0) ||
					isEmptyRangeFilter(newFilters[key])
				) {
					delete newCombinedFilters[key];
				}
			});

			const updated = {
				...prev,
				filters: { ...newCombinedFilters, ...cleanedNewFilters },
				page: 1,
			};
			writeSession(updated);
			return updated;
		});
	};

	const updateColumnsVisibility = (newVisibility: Record<string, boolean>) => {
		setParams((prev) => {
			const updated = {
				...prev,
				columnsVisibility: { ...prev.columnsVisibility, ...newVisibility },
			};
			writeColumnsVisibility(tableId, updated.columnsVisibility);
			return updated;
		});
	};

	return {
		tableParams: params,
		updateTableParams: updateParams,
		handleTableChange,
		handleSearch,
		clearFilters,
		updateFilters,
		updateColumnsVisibility,
	};
};
